import { SearchOrigin, toSearchOrigin, BUSINESS_EMAIL_URL_TOKEN, MAILBOX_SITE_LEVEL_REFERRAL, TLD_LIST_PAGE_PATH_TOKEN, WIXEL_REFERRAL, HARMONY_FLOW_URL_TOKEN, EDITOR_VIEW_MODE_URL_TOKEN } from '@wix/domains-serverless-interface';
import { ViewMode } from '../types';
const REFERRAL_FOCUS_WIDGET = 'dashboard-focus-widget';
const BNG_REFERRER_PATHS = ['/tools/business-name-generator', '/domain/result/bng'];
const WHOIS_REFERRER_PATHS = ['/domains/whois', '/domain/result/whois'];

// tld-list.com arrivals reach the /lp-<locale>/domain LP through the
// af_124005 Impact affiliate campaign; the LP referrer is their only marker.
const TLD_LIST_UTM_CAMPAIGN = 'af_124005';
const LP_DOMAIN_REFERRER_PATH = /^\/lp-[^/]+\/domain(\/|$)/;
const isTldListLpReferrer = () => {
  try {
    const referrer = new URL(document.referrer);
    return LP_DOMAIN_REFERRER_PATH.test(referrer.pathname) && referrer.searchParams.get('utm_campaign') === TLD_LIST_UTM_CAMPAIGN;
  } catch {
    return false;
  }
};
const isTldListPageUrl = () => {
  try {
    return window.location.pathname.includes(TLD_LIST_PAGE_PATH_TOKEN);
  } catch {
    return false;
  }
};
const isTldListSurface = () => isTldListPageUrl() || isTldListLpReferrer();
const getReferrerPathname = () => {
  try {
    return new URL(document.referrer).pathname;
  } catch {
    return '';
  }
};
const getCurrentUrl = () => {
  try {
    return window.location.href;
  } catch {
    return '';
  }
};
const isBusinessEmailReferrer = () => {
  try {
    return document.referrer.includes(BUSINESS_EMAIL_URL_TOKEN);
  } catch {
    return false;
  }
};
const isBusinessEmailUrl = () => getCurrentUrl().includes(BUSINESS_EMAIL_URL_TOKEN);

// 1052 (BM): search_origin keys off the my-mailboxes site-level referralInfo.
const isMailboxSiteLevelReferral = () => getCurrentUrl().includes(MAILBOX_SITE_LEVEL_REFERRAL);
const isWixelReferralUrl = () => getCurrentUrl().includes(`referralAdditionalInfo=${WIXEL_REFERRAL}`);
const isHarmonyEditorUrl = () => getCurrentUrl().includes(HARMONY_FLOW_URL_TOKEN);
const isEditorEmbedUrl = () => getCurrentUrl().includes(EDITOR_VIEW_MODE_URL_TOKEN);
const resolveEditorEmbedOrigin = isOnResultsPage => {
  if (isHarmonyEditorUrl()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_HARMONY_EDITOR : SearchOrigin.DOMAINS_GATE_HARMONY_EDITOR;
  }
  if (isEditorEmbedUrl()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_EDITOR : SearchOrigin.DOMAINS_GATE_EDITOR;
  }
  return undefined;
};
const resolveStorefrontOrigin = isOnResultsPage => {
  if (isOnResultsPage) {
    return SearchOrigin.STOREFRONT;
  }
  const referrerPath = getReferrerPathname();
  if (BNG_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
    return SearchOrigin.STOREFRONT_BNG;
  }
  if (WHOIS_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  return SearchOrigin.STOREFRONT;
};
export const resolveSearchOrigin = _ref => {
  let isOnResultsPage = _ref.isOnResultsPage,
    referralAdditionalInfo = _ref.referralAdditionalInfo,
    viewMode = _ref.viewMode,
    _ref$useReferralAddit = _ref.useReferralAdditionalInfoAsSearchOrigin,
    useReferralAdditionalInfoAsSearchOrigin = _ref$useReferralAddit === void 0 ? false : _ref$useReferralAddit;
  if (viewMode === ViewMode.BASE44) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BASE44 : SearchOrigin.DOMAINS_GATE_BASE44;
  }
  if (viewMode === ViewMode.FUNNEL_GATE || viewMode === ViewMode.FUNNEL_RESULTS_PAGE) {
    const source = new URLSearchParams(window.location.search).get('funnelSource');
    switch (source) {
      case 'non-chat':
        return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL_NON_AI_CHAT : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL_NON_AI_CHAT;
      case 'ai-chat':
      case null:
        return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL_AI_CHAT : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL_AI_CHAT;
      default:
        return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL;
    }
  }
  if (viewMode === ViewMode.WHOIS) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  if (viewMode === ViewMode.STOREFRONT_BNG) {
    return SearchOrigin.STOREFRONT_BNG;
  }
  if (viewMode === ViewMode.STOREFRONT) {
    if (referralAdditionalInfo === WIXEL_REFERRAL) {
      return SearchOrigin.STOREFRONT_WIXEL;
    }
    if (isBusinessEmailUrl() || isBusinessEmailReferrer()) {
      return SearchOrigin.STOREFRONT_BUSINESS_EMAIL;
    }
    if (isTldListSurface()) {
      return SearchOrigin.STOREFRONT_TLD_LIST;
    }
    return resolveStorefrontOrigin(isOnResultsPage);
  }
  // BM Business Email: URL carries the my-mailboxes site-level referralInfo.
  // Gate landing vs. results-page re-search get distinct origins.
  if (isMailboxSiteLevelReferral()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BUSINESS_EMAIL : SearchOrigin.DOMAINS_GATE_BUSINESS_EMAIL;
  }
  const editorEmbedOrigin = resolveEditorEmbedOrigin(isOnResultsPage);
  if (editorEmbedOrigin) {
    return editorEmbedOrigin;
  }
  if (viewMode === ViewMode.ACCOUNT_LEVEL_GATE || viewMode === ViewMode.ACCOUNT_LEVEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_ACCOUNT_LEVEL : SearchOrigin.DOMAINS_GATE_ACCOUNT_LEVEL;
  }
  const fallback = isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE : SearchOrigin.DOMAINS_GATE;
  if (useReferralAdditionalInfoAsSearchOrigin && !isOnResultsPage && referralAdditionalInfo) {
    return toSearchOrigin({
      value: referralAdditionalInfo,
      fallback
    });
  }
  if (isOnResultsPage) {
    if (!referralAdditionalInfo) {
      const referrerPath = getReferrerPathname();
      if (BNG_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
        return SearchOrigin.STOREFRONT_BNG;
      }
      if (WHOIS_REFERRER_PATHS.some(path => referrerPath.startsWith(path))) {
        return SearchOrigin.STOREFRONT_WHOIS;
      }
    }
    return SearchOrigin.DOMAINS_RESULTS_PAGE;
  }
  if (referralAdditionalInfo === REFERRAL_FOCUS_WIDGET) {
    return SearchOrigin.FOCUS_WIDGET;
  }
  return SearchOrigin.DOMAINS_GATE;
};
export const resolveViewPage = function (viewMode, isOnResultsPage) {
  if (isOnResultsPage === void 0) {
    isOnResultsPage = true;
  }
  if (viewMode === ViewMode.BASE44) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BASE44 : SearchOrigin.DOMAINS_GATE_BASE44;
  }
  if (viewMode === ViewMode.WHOIS) {
    return SearchOrigin.STOREFRONT_WHOIS;
  }
  if (viewMode === ViewMode.FUNNEL_GATE || viewMode === ViewMode.FUNNEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_MAIN_FUNNEL : SearchOrigin.DOMAINS_GATE_MAIN_FUNNEL;
  }
  if (viewMode === ViewMode.STOREFRONT || viewMode === ViewMode.STOREFRONT_BNG) {
    if (viewMode === ViewMode.STOREFRONT && isWixelReferralUrl()) {
      return SearchOrigin.STOREFRONT_WIXEL;
    }
    // Storefront Business Email: current URL (e.g. /business-email) contains business-email.
    if (viewMode === ViewMode.STOREFRONT && isBusinessEmailUrl()) {
      return SearchOrigin.STOREFRONT_BUSINESS_EMAIL;
    }
    if (viewMode === ViewMode.STOREFRONT && isTldListSurface()) {
      return SearchOrigin.STOREFRONT_TLD_LIST;
    }
    return viewMode;
  }
  if (isMailboxSiteLevelReferral() || isBusinessEmailUrl()) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_BUSINESS_EMAIL : SearchOrigin.DOMAINS_GATE_BUSINESS_EMAIL;
  }
  const editorEmbedViewPage = resolveEditorEmbedOrigin(isOnResultsPage);
  if (editorEmbedViewPage) {
    return editorEmbedViewPage;
  }
  if (viewMode === ViewMode.ACCOUNT_LEVEL_GATE || viewMode === ViewMode.ACCOUNT_LEVEL_RESULTS_PAGE) {
    return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE_ACCOUNT_LEVEL : SearchOrigin.DOMAINS_GATE_ACCOUNT_LEVEL;
  }
  return isOnResultsPage ? SearchOrigin.DOMAINS_RESULTS_PAGE : SearchOrigin.DOMAINS_GATE;
};
// 17:506 "Search Domain > Result Page Error" is published only in the legacy
// @wix/bi-logger-premium-data package, which this repo is migrating away
// from (/migrate-bi-events). This mirrors that generated function verbatim —
// swap to the @wix/bi-logger-premium-data-domains import once the event is
// added there.
export const searchDomainResultPageErrorSrc17Evid506 = params => ({
  evid: 506,
  src: 17,
  endpoint: '',
  params
});
export const shouldReport1054 = _ref2 => {
  let biGen = _ref2.biGen,
    handlerGen = _ref2.handlerGen,
    lastFiredGen = _ref2.lastFiredGen,
    pageNumber = _ref2.pageNumber,
    lastFiredPage = _ref2.lastFiredPage;
  if (biGen !== handlerGen) {
    return false;
  }
  return lastFiredGen < biGen || (pageNumber ?? 1) > lastFiredPage;
};
export const nowMs = () => typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
export const elapsedSince = startedAt => startedAt === undefined ? undefined : Math.max(0, Math.round(nowMs() - startedAt));
//# sourceMappingURL=useDomainSearchGateBi.utils.js.map